import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { HealthBar } from '../../HealthBar.js';
import { PALETTE, matte, colored } from '../../Style.js';

const GOLD = 0xd9a93a;
const BONE = 0xe8dcc0;

// A box stretched between two points, for wing bones.
function beam(from, to, thickness, color) {
  const a = new THREE.Vector3(...from);
  const b = new THREE.Vector3(...to);
  const dir = b.clone().sub(a);
  const geo = colored(new THREE.BoxGeometry(thickness, thickness, dir.length()), color);
  const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir.normalize());
  geo.applyMatrix4(new THREE.Matrix4().compose(a.add(b).multiplyScalar(0.5), quat, new THREE.Vector3(1, 1, 1)));
  return geo;
}

// Double-sided triangles built from point lists, so membranes show from below too.
function membrane(triangles, color) {
  const positions = [];
  for (const [a, b, c] of triangles) {
    positions.push(...a, ...b, ...c, ...a, ...c, ...b);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.computeVertexNormals();
  return colored(geo, color);
}

function lerp3(a, b, t) {
  return a.map((v, i) => v + (b[i] - v) * t);
}

function shade(color, lightness) {
  return new THREE.Color(color).offsetHSL(0, 0, lightness);
}

// Wyvern (helicopter): a low-poly winged beast with team-coloured scales and
// wing membranes that flap. The Dragon (super helicopter) is larger, horned,
// gold-spined and slower-beating.
export class Airplane {
  constructor(scene, unit, color, isSuper = false) {
    this.scene = scene;
    this.unit = unit;
    this.color = color;
    this.isSuper = isSuper;
    this.mesh = null;
    this.geometries = [];
    this.lastPosition = null;
    this.targetRotation = 0;
    this.currentBank = 0;
    this.flapPhase = Math.random() * Math.PI * 2; // So flocks don't flap in unison
    this.healthBar = null;
    this.maxHealth = isSuper ? 90 : 30; // SuperHelicopterHealth vs AirplaneHealth
    this.create();
  }

  create() {
    // Scale factor for super helicopters (1.3x larger)
    const scale = this.isSuper ? 1.3 : 1.0;
    const material = matte(0xffffff, { vertexColors: true });

    // Create parent group for positioning and yaw rotation
    this.mesh = new THREE.Group();

    // Create a yaw group (rotates around Y axis for heading)
    this.yawGroup = new THREE.Group();
    this.mesh.add(this.yawGroup);

    // Create a pitch/bank group for tilting; the beast faces +Z
    this.bankGroup = new THREE.Group();
    this.bankGroup.scale.setScalar(scale);
    this.yawGroup.add(this.bankGroup);

    this.bodyMesh = new THREE.Mesh(this.track(mergeGeometries(this.buildBody())), material);
    this.bodyMesh.castShadow = true;
    this.bodyMesh.receiveShadow = true;
    this.bankGroup.add(this.bodyMesh);

    // Tail in two jointed segments so it can sway
    const tail = this.buildTail();
    this.tailGroup = new THREE.Group();
    this.tailGroup.position.set(0, 0.05, -0.95);
    this.tailGroup.add(new THREE.Mesh(this.track(mergeGeometries(tail.base)), material));
    this.bankGroup.add(this.tailGroup);

    this.tailTipGroup = new THREE.Group();
    this.tailTipGroup.position.z = -1.2;
    this.tailTipGroup.add(new THREE.Mesh(this.track(mergeGeometries(tail.tip)), material));
    this.tailGroup.add(this.tailTipGroup);

    // Wings hinge at the shoulders
    this.wings = [];
    for (const side of [-1, 1]) {
      const wing = new THREE.Group();
      wing.position.set(side * 0.5, 0.35, 0.3);
      const wingMesh = new THREE.Mesh(this.track(mergeGeometries(this.buildWing(side))), material);
      wingMesh.castShadow = true;
      wing.add(wingMesh);
      wing.userData.side = side;
      this.bankGroup.add(wing);
      this.wings.push(wing);
    }

    // Create health bar above the beast (on parent group so it stays upright)
    this.healthBar = new HealthBar(this.maxHealth, 3 * scale, 0.35);
    this.healthBar.getGroup().position.y = 3 * scale;
    this.mesh.add(this.healthBar.getGroup());

    // Set initial position
    this.mesh.position.set(this.unit.position.x, this.unit.position.y, this.unit.position.z);
    this.lastPosition = { ...this.unit.position };

    this.scene.add(this.mesh);
  }

  track(geometry) {
    this.geometries.push(geometry);
    return geometry;
  }

  buildBody() {
    const team = this.color;
    const dark = shade(team, -0.14);
    const belly = this.isSuper ? 0xe6c46a : 0xe6d3a3;
    const spine = this.isSuper ? GOLD : dark;
    const parts = [
      // Torso and lighter belly
      colored(new THREE.SphereGeometry(0.75, 7, 5), team, { scale: [0.95, 0.85, 1.5] }),
      colored(new THREE.SphereGeometry(0.7, 7, 4), belly, { y: -0.22, z: 0.05, scale: [0.8, 0.6, 1.35] }),
      // Neck rising forward to the head
      colored(new THREE.CylinderGeometry(0.26, 0.4, 1.3, 6), team, { y: 0.45, z: 1.4, rx: Math.PI / 2 - 0.55 }),
      // Head, snout, jaw and glowing eyes
      colored(new THREE.BoxGeometry(0.5, 0.42, 0.72), team, { y: 0.85, z: 2.15, rx: 0.12 }),
      colored(new THREE.BoxGeometry(0.36, 0.26, 0.5), team, { y: 0.78, z: 2.6, rx: 0.15 }),
      colored(new THREE.BoxGeometry(0.32, 0.1, 0.55), belly, { y: 0.6, z: 2.5, rx: 0.15 }),
      colored(new THREE.BoxGeometry(0.08, 0.08, 0.1), PALETTE.lanternGlow, { x: 0.24, y: 0.94, z: 2.3 }),
      colored(new THREE.BoxGeometry(0.08, 0.08, 0.1), PALETTE.lanternGlow, { x: -0.24, y: 0.94, z: 2.3 })
    ];

    // Horns sweeping back from the head
    const hornLength = this.isSuper ? 0.75 : 0.35;
    for (const side of [-1, 1]) {
      parts.push(colored(new THREE.ConeGeometry(0.09, hornLength, 4), this.isSuper ? GOLD : BONE, {
        x: side * 0.17, y: 1.08 + hornLength * 0.25, z: 1.95 - hornLength * 0.4, rx: -1.04, rz: -side * 0.2
      }));
    }

    // Legs tucked under the belly, with claws
    for (const side of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(0.26, 0.42, 0.5), dark, { x: side * 0.42, y: -0.45, z: -0.35, rx: 0.6 }));
      parts.push(colored(new THREE.BoxGeometry(0.22, 0.12, 0.34), this.isSuper ? GOLD : PALETTE.iron, {
        x: side * 0.42, y: -0.68, z: -0.6
      }));
    }

    // Ridge spikes along the neck and back
    const spikes = this.isSuper
      ? [[0.62, 0.8], [0.72, 0.35], [0.74, -0.1], [0.68, -0.55], [0.55, -0.95], [0.8, 1.3], [1.05, 1.7]]
      : [[0.66, 0.5], [0.7, -0.1], [0.6, -0.7]];
    const spikeHeight = this.isSuper ? 0.42 : 0.26;
    for (const [y, z] of spikes) {
      parts.push(colored(new THREE.ConeGeometry(0.1, spikeHeight, 4), spine, { y: y + spikeHeight * 0.3, z, rx: -0.5 }));
    }

    return parts;
  }

  buildTail() {
    const team = this.color;
    const spade = this.isSuper ? GOLD : shade(team, -0.14);
    const base = [colored(new THREE.CylinderGeometry(0.16, 0.34, 1.25, 6), team, { z: -0.6, rx: -Math.PI / 2 })];
    const tip = [
      colored(new THREE.CylinderGeometry(0.05, 0.16, 1.05, 5), team, { z: -0.5, rx: -Math.PI / 2 }),
      colored(new THREE.OctahedronGeometry(0.34), spade, { z: -1.1, scale: [1, 0.18, 1.2] })
    ];
    if (this.isSuper) {
      for (const z of [-0.3, -0.85]) {
        base.push(colored(new THREE.ConeGeometry(0.08, 0.3, 4), GOLD, { y: 0.3, z, rx: -0.5 }));
      }
      tip.push(colored(new THREE.ConeGeometry(0.06, 0.24, 4), GOLD, { y: 0.17, z: -0.35, rx: -0.5 }));
    }
    return { base, tip };
  }

  // One wing extending along side * X from the shoulder hinge
  buildWing(side) {
    const p = (x, y, z) => [side * x, y, z];
    const shoulder = p(0, 0, 0);
    const elbow = p(1.5, 0.25, 0.35);
    const wrist = p(2.9, 0.1, 0.1);
    const f1 = p(3.4, -0.05, -0.9);
    const f2 = p(2.5, -0.05, -1.55);
    const f3 = p(1.4, -0.05, -1.5);
    const root = p(0.1, 0, -1.2);

    // Scalloped trailing edge between the finger bones
    const m12 = lerp3(lerp3(f1, f2, 0.5), wrist, 0.22);
    const m23 = lerp3(lerp3(f2, f3, 0.5), wrist, 0.22);
    const m3r = lerp3(lerp3(f3, root, 0.5), elbow, 0.18);

    const bone = this.isSuper ? shade(this.color, -0.25) : PALETTE.woodDark;
    const parts = [
      membrane([
        [shoulder, elbow, root], [elbow, m3r, root], [elbow, f3, m3r], [elbow, wrist, f3],
        [wrist, m23, f3], [wrist, f2, m23], [wrist, m12, f2], [wrist, f1, m12]
      ], shade(this.color, 0.05)),
      beam(shoulder, elbow, 0.18, this.color),
      beam(elbow, wrist, 0.13, bone),
      beam(wrist, f1, 0.06, bone),
      beam(wrist, f2, 0.06, bone),
      beam(wrist, f3, 0.06, bone),
      // Thumb claw at the wrist
      colored(new THREE.ConeGeometry(0.07, 0.3, 4), this.isSuper ? GOLD : BONE, {
        x: wrist[0], y: wrist[1], z: wrist[2] + 0.2, rx: Math.PI / 2
      })
    ];
    if (this.isSuper) {
      // Gold talons on the finger tips
      for (const f of [f1, f2, f3]) {
        parts.push(colored(new THREE.BoxGeometry(0.12, 0.12, 0.12), GOLD, { x: f[0], y: f[1], z: f[2] }));
      }
    }
    return parts;
  }

  // Wing beats, a gentle body bob and a swaying tail
  animate() {
    const t = performance.now() / 1000;
    const speed = this.isSuper ? 3.6 : 5.0;
    const beat = Math.sin(t * speed + this.flapPhase);
    const flap = 0.1 + beat * (this.isSuper ? 0.6 : 0.55);

    for (const wing of this.wings) {
      wing.rotation.z = wing.userData.side * flap;
    }
    // The body rises on the downstroke
    this.bankGroup.position.y = -beat * 0.15;

    const sway = t * 1.6 + this.flapPhase;
    this.tailGroup.rotation.y = Math.sin(sway) * 0.15;
    this.tailGroup.rotation.x = beat * 0.06;
    this.tailTipGroup.rotation.y = Math.sin(sway - 0.9) * 0.3;
  }

  updatePosition(position) {
    if (this.mesh && this.yawGroup && this.bankGroup) {
      // Calculate rotation based on movement direction
      if (this.lastPosition) {
        const dx = position.x - this.lastPosition.x;
        const dz = position.z - this.lastPosition.z;
        const distanceMoved = Math.sqrt(dx * dx + dz * dz);

        // Only update rotation if we've moved significantly
        if (distanceMoved > 0.01) {
          // Calculate target rotation to face movement direction
          // atan2(dx, dz) gives angle from +Z axis, we want to face the movement direction
          const newTargetRotation = Math.atan2(dx, dz);

          // Calculate turn rate for banking effect
          let turnRate = newTargetRotation - this.targetRotation;
          while (turnRate > Math.PI) turnRate -= Math.PI * 2;
          while (turnRate < -Math.PI) turnRate += Math.PI * 2;

          // Target bank angle based on turn rate (bank into turns)
          const targetBank = Math.max(-0.5, Math.min(0.5, -turnRate * 2));
          this.currentBank += (targetBank - this.currentBank) * 0.1;

          this.targetRotation = newTargetRotation;
        } else {
          // Reduce bank when not turning
          this.currentBank *= 0.95;
        }
      }

      // Smoothly interpolate yaw rotation (apply to yaw group)
      let rotationDiff = this.targetRotation - this.yawGroup.rotation.y;
      while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
      while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
      this.yawGroup.rotation.y += rotationDiff * 0.15;

      // Apply banking to bank group (roll around local Z axis)
      this.bankGroup.rotation.z = this.currentBank;

      this.animate();

      // Store last position
      this.lastPosition = { x: position.x, y: position.y, z: position.z };
    }
  }

  // Update health bar
  updateHealth(health, camera) {
    if (this.healthBar) {
      this.healthBar.update(health, camera);
    }
  }

  remove() {
    if (this.healthBar) {
      this.healthBar.dispose();
    }
    if (this.mesh) {
      this.scene.remove(this.mesh);
    }
    // The vertex-colour material is shared via matte(); only geometries are ours
    this.geometries.forEach(geometry => geometry.dispose());
    this.geometries = [];
  }
}
